import { z } from "zod";

// ---------------------------------------------------------------------------
// Condition operators — shared across all workflow engines
// ---------------------------------------------------------------------------
// Extracted from email-marketing + marketing to eliminate duplication.
// Both packages now import from @ease/workflows.

export const CONDITION_OPS = [
  // Equality
  "eq",
  "neq",
  // Numeric comparison
  "gt",
  "gte",
  "lt",
  "lte",
  // String matching
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "matches_regex",
  // Existence
  "exists",
  "not_exists",
  // Set membership
  "in",
  "not_in",
  // Range
  "between",
] as const;

export type ConditionOp = (typeof CONDITION_OPS)[number];

export const ConditionSchema = z.object({
  field: z.string(),
  op: z.enum(CONDITION_OPS),
  value: z.unknown(),
});

export type Condition = z.infer<typeof ConditionSchema>;

// ---------------------------------------------------------------------------
// ReDoS protection for matches_regex
// ---------------------------------------------------------------------------

const MAX_REGEX_LENGTH = 200;

/** Reject regex patterns known to cause catastrophic backtracking */
function isSafeRegex(pattern: string): boolean {
  if (pattern.length > MAX_REGEX_LENGTH) return false;
  // Reject nested quantifiers: (a+)+ , (a*)*  , (a+)*
  if (/(\([^)]*[+*]\)[+*])/.test(pattern)) return false;
  // Reject repeated wildcards: .*.*  , .+.+
  if (/(\.\*){2,}|(\.\+){2,}/.test(pattern)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Field resolution
// ---------------------------------------------------------------------------

export interface ConditionContact {
  id: string;
  email: string;
  tags: string[];
  customFields: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ConditionLogger {
  warn(msg: string, meta?: Record<string, unknown>): void;
}

function resolveFieldValue(
  field: string,
  contact: ConditionContact,
): unknown {
  if (field in contact) {
    return contact[field];
  }
  return contact.customFields[field];
}

// ---------------------------------------------------------------------------
// Condition evaluator — 15 operators
// ---------------------------------------------------------------------------

export function evaluateCondition(
  condition: Condition,
  contact: ConditionContact,
  logger?: ConditionLogger,
): boolean {
  const { field, op, value } = condition;

  // Special case: tags + contains
  if (field === "tags" && op === "contains") {
    return contact.tags.includes(value as string);
  }

  const fieldValue = resolveFieldValue(field, contact);

  switch (op) {
    // --- Equality ---
    case "eq":
      return fieldValue === value;
    case "neq":
      return fieldValue !== value;

    // --- Numeric comparison ---
    case "gt":
      return (fieldValue as number) > (value as number);
    case "gte":
      return (fieldValue as number) >= (value as number);
    case "lt":
      return (fieldValue as number) < (value as number);
    case "lte":
      return (fieldValue as number) <= (value as number);

    // --- String matching ---
    case "contains": {
      if (typeof fieldValue === "string") {
        return fieldValue.includes(value as string);
      }
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(value);
      }
      return false;
    }
    case "not_contains": {
      if (typeof fieldValue === "string") {
        return !fieldValue.includes(value as string);
      }
      if (Array.isArray(fieldValue)) {
        return !fieldValue.includes(value);
      }
      return true;
    }
    case "starts_with": {
      if (typeof fieldValue !== "string") return false;
      return fieldValue.startsWith(value as string);
    }
    case "ends_with": {
      if (typeof fieldValue !== "string") return false;
      return fieldValue.endsWith(value as string);
    }
    case "matches_regex": {
      if (typeof fieldValue !== "string" || typeof value !== "string") return false;
      if (!isSafeRegex(value)) {
        logger?.warn("evaluateCondition: regex rejected (unsafe pattern)", { field, pattern: value });
        return false;
      }
      try {
        return new RegExp(value).test(fieldValue);
      } catch {
        logger?.warn("evaluateCondition: invalid regex pattern", { field, pattern: value });
        return false;
      }
    }

    // --- Existence ---
    case "exists":
      return fieldValue !== undefined && fieldValue !== null;
    case "not_exists":
      return fieldValue === undefined || fieldValue === null;

    // --- Set membership ---
    case "in": {
      if (!Array.isArray(value)) {
        logger?.warn("evaluateCondition: 'in' operator requires array value", { field });
        return false;
      }
      return value.includes(fieldValue);
    }
    case "not_in": {
      if (!Array.isArray(value)) {
        logger?.warn("evaluateCondition: 'not_in' operator requires array value", { field });
        return false;
      }
      return !value.includes(fieldValue);
    }

    // --- Range ---
    case "between": {
      if (!Array.isArray(value) || value.length !== 2) {
        logger?.warn("evaluateCondition: 'between' operator requires [min, max] array", { field });
        return false;
      }
      const num = fieldValue as number;
      return num >= (value[0] as number) && num <= (value[1] as number);
    }

    default: {
      logger?.warn(`evaluateCondition: unknown operator "${op}"`, { field, op });
      return false;
    }
  }
}
